import React from 'react';

// Same colors as the map uses
const supplyColors = [
  '#2196F3', '#FF9800', '#4CAF50', '#E91E63', '#9C27B0', '#FF5722',
  '#00BCD4', '#795548', '#607D8B', '#FFC107', '#3F51B5', '#8BC34A'
];

export default function SolutionDisplay({ solution, isolationInfo, locations, setLocations }) {
  if (!solution) return null;

  return (
    <div className="solution-display">
      <h4>Solution Results:</h4>
      <div className="solution-summary">
        <p><strong>Method:</strong> {solution.method}</p>
        <p><strong>Total Cost:</strong> {solution.total_cost?.toFixed(2)} km</p>
      </div>
      
      <h5>Allocation Matrix:</h5>
      
      {/* Unbalanced problem warning */}
      {(() => {
        const supplyLocs = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
        const demandLocs = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
        const totalSupply = supplyLocs.reduce((sum, loc) => sum + Number(loc.quantity || 0), 0);
        const totalDemand = demandLocs.reduce((sum, loc) => sum + Number(loc.quantity || 0), 0);
        const isUnbalanced = Math.abs(totalSupply - totalDemand) > 0.01;
        
        if (isUnbalanced) {
          return (
            <div className="unbalanced-info" style={{
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: '4px', 
              padding: '8px', 
              marginBottom: '10px',
              fontSize: '0.9em'
            }}>
              <strong>Unbalanced Problem:</strong> Total Supply ({totalSupply}) ≠ Total Demand ({totalDemand})
              <br />
              <small>Virtual {totalSupply > totalDemand ? 'Demand' : 'Supply'} added to balance the problem.</small>
            </div>
          );
        }
        return null;
      })()}

      {/* Allocation Matrix */}
      <div className="allocation-matrix">
        <table style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: '8px 4px',
          marginTop: '10px'
        }}>
          <thead>
            <tr>
              <th>Supply / Demand</th>
              {(() => {
                const demandLocs = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
                const allocationCols = solution.allocations?.[0]?.length || 0;
                
                const demandHeaders = demandLocs.map((loc, j) => (
                  <th key={loc.id}>{loc.number || loc.name || `D${j+1}`}</th>
                ));
                
                const hasVirtualDemand = allocationCols > demandLocs.length;
                const virtualHeader = hasVirtualDemand ? 
                  <th key="virtual-demand">Virtual Demand</th> : null;
                
                return [...demandHeaders, virtualHeader];
              })()}
            </tr>
          </thead>
          <tbody>
            {solution.allocations?.map((row, i) => {
              const supplyLocs = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
              const isVirtualSupply = i >= supplyLocs.length;
              const actualSupply = isVirtualSupply ? null : supplyLocs[i];
              const supplyLabel = isVirtualSupply ? 'Virtual Supply' : (actualSupply?.number || actualSupply?.name || `S${i+1}`);
              
              return (
                <tr key={i}>
                  <td><strong>{supplyLabel}</strong></td>
                  {row.map((value, j) => (
                    <td key={j}>{value > 0 ? Math.round(value) : '-'}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Route Information */}
      {solution.routes && solution.routes.length > 0 && (
        <RouteInfo 
          solution={solution}
          locations={locations}
          supplyColors={supplyColors}
        />
      )}

      {/* Isolation Analysis */}
      {isolationInfo && (isolationInfo.hasIsolation || isolationInfo.unusedSupplies.length > 0 || isolationInfo.partiallyUnmetDemands.length > 0) && (
        <IsolationAnalysis isolationInfo={isolationInfo} />
      )}

      {/* Fix button for tpType issues */}
      <TypeFixButtons locations={locations} setLocations={setLocations} />
    </div>
  );
}

function RouteInfo({ solution, locations, supplyColors }) {
  const supplies = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
  const demands = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
  
  const realSupplyToRealDemandRoutes = solution.routes.filter(route => 
    route.quantity > 0 && 
    route.supply_index < supplies.length && 
    route.demand_index < demands.length
  );
  
  const routesBySupply = {};
  realSupplyToRealDemandRoutes.forEach(route => {
    const supplyIndex = route.supply_index;
    if (!routesBySupply[supplyIndex]) {
      routesBySupply[supplyIndex] = [];
    }
    routesBySupply[supplyIndex].push(route);
  });

  const overallTotalDistance = Object.values(routesBySupply).flat().reduce((sum, route) => sum + route.distance, 0);

  return (
    <div className="routes-info">
      <h5>Direct Delivery Routes:</h5>
      <p>Total Distance: {overallTotalDistance.toFixed(2)} km</p>
      {Object.entries(routesBySupply).map(([supplyIndex, supplyRoutes]) => {
        const supplyIndexNum = parseInt(supplyIndex);
        const totalQuantity = supplyRoutes.reduce((sum, route) => sum + route.quantity, 0);
        const totalDistance = supplyRoutes.reduce((sum, route) => sum + route.distance, 0);
        
        const actualSupply = supplies[supplyIndexNum];
        const supplyNumber = actualSupply?.number || actualSupply?.name || `S${supplyIndexNum + 1}`;
        const routeColor = supplyColors[supplyIndexNum % supplyColors.length];
        
        const cardStyle = {
          marginBottom: '15px', 
          padding: '10px', 
          border: `2px solid ${routeColor}`, 
          borderRadius: '5px', 
          backgroundColor: `${routeColor}10`
        };
        
        return (
          <div key={supplyIndex} className="supply-route" style={cardStyle}>
            <h6 style={{color: routeColor, margin: '0 0 5px 0', display: 'flex', alignItems: 'center'}}>
              <span style={{width: '12px', height: '12px', backgroundColor: routeColor, borderRadius: '50%', marginRight: '8px'}}></span>
              {supplyNumber}
            </h6>
            <div style={{fontSize: '0.9em', color: '#666', marginBottom: '8px'}}>
              Total Quantity: <strong>{totalQuantity}</strong> units | 
              Total Distance: <strong>{totalDistance.toFixed(2)}</strong> km | 
              Flows: <strong>{supplyRoutes.length}</strong>
            </div>
            <div style={{fontSize: '0.85em'}}>
              {supplyRoutes.map((route, idx) => {
                const actualDemand = demands[route.demand_index];
                const demandNumber = actualDemand?.number || actualDemand?.name || route.to_location || `D${route.demand_index + 1}`;
                
                return (
                  <div key={idx} style={{marginBottom: '4px', display: 'flex', alignItems: 'center'}}>
                    <span style={{color: routeColor, fontWeight: 'bold'}}>{supplyNumber}</span>
                    <span style={{color: routeColor, margin: '0 4px'}}> → </span>
                    <span style={{color: '#333', backgroundColor: `${routeColor}20`, padding: '2px 6px', borderRadius: '3px'}}>
                      {demandNumber} ({route.quantity} units, {route.distance.toFixed(2)}km)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Virtual routes */}
      <VirtualRoutes solution={solution} locations={locations} />
    </div>
  );
}

function VirtualRoutes({ solution, locations }) {
  const actualSupplies = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'supply');
  const actualDemands = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
  
  const virtualDemandRoutes = solution.routes.filter(route => 
    route.quantity > 0 && 
    route.supply_index < actualSupplies.length && 
    route.demand_index >= actualDemands.length
  );

  const virtualSupplyRoutes = solution.routes.filter(route => 
    route.supply_index >= actualSupplies.length && route.quantity > 0
  );

  return (
    <>
      {/* Virtual Demand Routes */}
      {virtualDemandRoutes.length > 0 && (
        <div className="virtual-demand-routes-info" style={{marginTop: '15px'}}>
          <h5>Virtual Demand Routes:</h5>
          <div style={{padding: '10px', borderRadius: '5px'}}>
            {virtualDemandRoutes.map((route, idx) => {
              const actualSupply = actualSupplies[route.supply_index];
              const supplyNumber = actualSupply?.number || actualSupply?.name || `S${route.supply_index + 1}`;
              
              return (
                <div key={idx} style={{fontSize: '0.9em', marginBottom: '3px'}}>
                  <strong>{supplyNumber}</strong> → <strong>Virtual Demand {route.demand_index - actualDemands.length + 1}</strong>: {route.quantity} units
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Virtual Supply Routes */}
      {virtualSupplyRoutes.length > 0 && (
        <div className="virtual-routes-info" style={{marginTop: '15px'}}>
          <h5>Virtual Supply Routes:</h5>
          <div style={{padding: '10px', borderRadius: '5px'}}>
            {virtualSupplyRoutes.map((route, idx) => {
              const demands = locations.filter(loc => (loc.tpType || '').toLowerCase() === 'demand');
              const actualDemand = demands[route.demand_index];
              const demandNumber = actualDemand?.number || actualDemand?.name || route.to_location || `D${route.demand_index + 1}`;
              
              return (
                <div key={idx} style={{fontSize: '0.9em', marginBottom: '3px'}}>
                  <strong>Virtual Supply</strong> → <strong>{demandNumber}</strong>: {route.quantity} units
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function IsolationAnalysis({ isolationInfo }) {
  return (
    <div className="isolation-info" style={{marginTop: '15px'}}>
      <h5>Isolation & Unused Capacity Analysis:</h5>
      
      {/* Isolated supplies */}
      {isolationInfo.isolatedSupplies.length > 0 && (
        <div style={{marginBottom: '10px', padding: '10px', backgroundColor: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '5px'}}>
          <h6 style={{color: '#d32f2f', margin: '0 0 5px 0'}}>
            Isolated Supplies ({isolationInfo.isolatedSupplies.length})
          </h6>
          {isolationInfo.isolatedSupplies.map((supply, idx) => (
            <div key={idx} style={{fontSize: '0.9em', marginBottom: '3px'}}>
              <strong>{supply.location.number || supply.location.name}</strong> - 
              Quantity: {supply.quantity} units (completely unused)
            </div>
          ))}
          <div style={{fontSize: '0.8em', color: '#666', marginTop: '5px'}}>
            These supply points have no routes assigned and remain isolated.
          </div>
        </div>
      )}
      
      {/* Isolated demands */}
      {isolationInfo.isolatedDemands.length > 0 && (
        <div style={{marginBottom: '10px', padding: '10px', backgroundColor: '#ffebee', border: '1px solid #ffcdd2', borderRadius: '5px'}}>
          <h6 style={{color: '#d32f2f', margin: '0 0 5px 0'}}>
            Isolated Demands ({isolationInfo.isolatedDemands.length})
          </h6>
          {isolationInfo.isolatedDemands.map((demand, idx) => (
            <div key={idx} style={{fontSize: '0.9em', marginBottom: '3px'}}>
              <strong>{demand.location.number || demand.location.name}</strong> - 
              Quantity: {demand.quantity} units (completely unmet)
            </div>
          ))}
          <div style={{fontSize: '0.8em', color: '#666', marginTop: '5px'}}>
            These demand points receive no supply and remain isolated.
          </div>
        </div>
      )}
      
      {/* Partially unused supplies */}
      {isolationInfo.unusedSupplies.length > 0 && (
        <div style={{marginBottom: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '5px'}}>
          <h6 style={{color: '#856404', margin: '0 0 5px 0'}}>
            Partially Unused Supplies ({isolationInfo.unusedSupplies.length})
          </h6>
          {isolationInfo.unusedSupplies.map((supply, idx) => (
            <div key={idx} style={{fontSize: '0.9em', marginBottom: '3px'}}>
              <strong>{supply.location.number || supply.location.name}</strong> - 
              Used: {supply.allocated}/{supply.quantity} units ({supply.unused} unused)
            </div>
          ))}
        </div>
      )}
      
      {/* Partially unmet demands */}
      {isolationInfo.partiallyUnmetDemands.length > 0 && (
        <div style={{marginBottom: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '5px'}}>
          <h6 style={{color: '#856404', margin: '0 0 5px 0'}}>
            Partially Unmet Demands ({isolationInfo.partiallyUnmetDemands.length})
          </h6>
          {isolationInfo.partiallyUnmetDemands.map((demand, idx) => (
            <div key={idx} style={{fontSize: '0.9em', marginBottom: '3px'}}>
              <strong>{demand.location.number || demand.location.name}</strong> - 
              Received: {demand.received}/{demand.quantity} units ({demand.unmet} unmet)
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Type fix buttons component
function TypeFixButtons({ locations, setLocations }) {
  const needsFixing = locations.filter(loc => !loc.tpType || loc.tpType === 'undefined');
  
  if (needsFixing.length === 0) return null;

  const handleFixType = (newType) => {
    const fixedLocations = locations.map(loc => {
      if (!loc.tpType || loc.tpType === 'undefined') {
        return { ...loc, tpType: newType };
      }
      return loc;
    });
    setLocations(fixedLocations);
  };

  return (
    <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '5px'}}>
      <h6>Oops! {needsFixing.length} points need a type</h6>
      <div style={{fontSize: '0.85em', marginBottom: '8px'}}>
        Missing types: {needsFixing.map(loc => loc.name || loc.number || 'unnamed').join(', ')}
      </div>
      <div style={{display: 'flex', gap: '8px'}}>
        <button 
          onClick={() => handleFixType('demand')}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '6px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9em'
          }}
        >
          Set All to Demand
        </button>
        <button 
          onClick={() => handleFixType('supply')}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            padding: '6px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9em'
          }}
        >
          Set All to Supply
        </button>
      </div>
    </div>
  );
}
